import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { setAdminPassword } from './adminAuth';

/**
 * Phase 16.5.2 — Admin-Login-Page bei /admin.
 * Prüft Passwort gegen GET /api/admin/verify; bei 200 → /admin/dashboard.
 */
export default function AdminLogin() {
  const navigate = useNavigate();
  const [pw, setPw] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/verify', {
        headers: { Authorization: `Admin ${pw}` },
      });
      if (res.ok) {
        setAdminPassword(pw);
        navigate('/admin/dashboard', { replace: true });
      } else if (res.status === 401) {
        setError('Passwort falsch');
      } else {
        setError(`Server-Fehler (HTTP ${res.status})`);
      }
    } catch (e) {
      setError(`Verbindungsfehler: ${String(e)}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg-primary px-4">
      <div className="w-full max-w-sm rounded-xl border border-border-strong bg-bg-secondary shadow-2xl">
        <header className="flex items-center gap-3 border-b border-border-subtle px-6 py-4">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-emergency">
            <Lock className="h-5 w-5 text-white" aria-hidden />
          </span>
          <div>
            <p className="font-display text-lg font-bold">Admin · Login</p>
            <p className="text-xs text-fg-muted">Dispatch24 Beta-Verwaltung</p>
          </div>
        </header>

        <form onSubmit={onSubmit} className="space-y-4 px-6 py-6">
          <div>
            <label htmlFor="admin-pw" className="mb-2 block text-sm font-semibold">
              Passwort
            </label>
            <input
              id="admin-pw"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoComplete="current-password"
              data-testid="admin-pw-input"
              className="w-full rounded-md border border-border-subtle bg-bg-primary p-3 text-sm text-fg-primary outline-none transition focus:border-accent-dispatch"
            />
          </div>

          {error && (
            <div className="rounded-md border border-accent-emergency/40 bg-accent-emergency/10 p-3 text-sm text-accent-emergency">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || pw.length === 0}
            data-testid="admin-login-submit"
            className="w-full rounded-md bg-accent-emergency px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-accent-emergency-hover disabled:cursor-not-allowed disabled:bg-bg-elevated disabled:text-fg-muted"
          >
            {submitting ? 'Prüfe …' : 'Einloggen'}
          </button>
        </form>
      </div>
    </main>
  );
}
